<template>
  <ModalCreateEditSample
    v-show="modalInfoSample"
    :originalReference="selectedReference"
    @close="closeInformationSampleModal"
    @openInventoryImageModal="openInventoryImageModal"
    @buildPrimary="buildPrimaryReferenceTransaction"
    @deletePrimary="deletePrimaryReferenceTransaction"
    @deleteSecondary="deleteSecondaryReferenceTransaction"
  />
  <ModalCreateEditInventoryImage
    v-show="modalInventoryImage"
    @closeImageInventory="closeInventoryImageModal"
    @secondary="buildSecondaryReferenceTransaction"
    @deleteSecondary="deleteSecondaryReferenceTransaction"
    :additional="selectedAdditional"
  />
  <ModalConfirmation
    :message="confirmMessage"
    @accept="closeConfirmModalAsync(deletePrimaryReferenceTransaction)"
    @deny="closeConfirmModalAsync"
    v-show="showConfirmModal"
  />

  <div class="wrapper-sample-book">
    <div class="above-info">
      <p>Muestrario</p>
      <div class="wrapper-buttons">
        <StandardButton
          :buttonLabel="'Crear referencia'"
          @click="createNewReference"
        />
        <a :href="route" class="wrapper-button">
          <StandardButton :buttonLabel="'Descargar reporte de imágenes'" />
        </a>
      </div>
    </div>

    <ul class="wrapper-samples">
      <li class="sample" v-for="(reference, index) in references" :key="index">
        <div class="wrapper-image">
          <img :src="reference.main" alt="" class="cloth-image" />

          <div class="wrapper-buttons">
            <EditButton @click="editReference(reference)" />
            <DeleteButton
              @click="selectDeletedReference(reference)"
              :type="'Delete'"
            />
          </div>
        </div>

        <div class="wrapper-name">
          <p>Ref. {{ reference.reference }}</p>
          <p>{{ reference.description }}</p>
        </div>
      </li>
    </ul>
  </div>
</template>

<script lang="ts">
import { defineComponent, inject } from "vue";
import ModalCreateEditSample from "./components/ModalCreateEditSample.vue";
import ModalConfirmation from "@/components/commons/modals/ModalConfirmation.vue";
import { sampleBookUse } from "./use/sampleBookUse";
import StandardButton from "@/components/commons/controls/StandardButton.vue";
import EditButton from "@/components/commons/controls/EditButton.vue";
import DeleteButton from "@/components/commons/controls/DeleteButton.vue";
import { spinnerProcessKey, successProcessKey } from "@/main";
import { confirmCancelMessageManager } from "@/ux/utils/managers/confirmCancelMessageManager";
import { Reference } from "@/business/inventory/domain/inventory";
import ModalCreateEditInventoryImage from "@/ux/sampleBook/components/ModalCreateEditInventoryImage.vue";

export default defineComponent({
  name: "SampleBookView",
  components: {
    ModalCreateEditInventoryImage,
    ModalCreateEditSample,
    ModalConfirmation,
    StandardButton,
    EditButton,
    DeleteButton,
  },

  setup() {
    const successProcess = inject(successProcessKey)!;
    const { showSuccessModal } = successProcess;

    const spinnerProcess = inject(spinnerProcessKey)!;
    const { offSpinnerState, onSpinnerState } = spinnerProcess;

    const {
      showConfirmModal,
      openConfirmModal,
      closeConfirmModalAsync,
      confirmMessage,
    } = confirmCancelMessageManager();

    const {
      modalInfoSample,
      closeInformationSampleModal,
      modalInventoryImage,
      closeInventoryImageModal,
      references,
      buildPrimaryReferenceOperation,
      buildSecondaryReferenceOperation,
      selectedReference,
      deletePrimaryReferenceOperation,
      deleteSecondaryReferenceOperation,
      createNewReference,
      editReference,
      openInventoryImageModal,
      selectedAdditional,
      route,
    } = sampleBookUse();

    async function buildPrimaryReferenceTransaction(reference: {
      reference: string;
      description: string;
      image: File;
      message: string;
    }) {
      try {
        await onSpinnerState();

        await buildPrimaryReferenceOperation(
          reference.reference,
          reference.description,
          reference.image
        );

        await offSpinnerState();

        showSuccessModal(reference.message, closeInformationSampleModal);
      } catch (exception) {
        await offSpinnerState();
        throw exception;
      }
    }

    async function buildSecondaryReferenceTransaction(additional: {
      slider: File | null;
      bigImage: File | null;
      isEdit: boolean;
    }) {
      try {
        await onSpinnerState();
        await buildSecondaryReferenceOperation(
          additional.slider,
          additional.bigImage
        );
        await offSpinnerState();
        const message = additional.isEdit
          ? "Imágen de inventario editada correctamente"
          : "Imágen de inventario creada correctamente";
        showSuccessModal(message);
      } catch (exception) {
        await offSpinnerState();
        throw exception;
      }
    }

    async function selectDeletedReference(reference: Reference) {
      selectedReference.value = reference;
      openConfirmModal("¿Estás seguro de eliminar la referencia?");
    }

    async function deletePrimaryReferenceTransaction(): Promise<void> {
      try {
        await onSpinnerState();
        await deletePrimaryReferenceOperation();
        await offSpinnerState();
        showSuccessModal("La referencia ha sido eliminada correctamente.");
      } catch (exception) {
        await offSpinnerState();
        throw exception;
      }
    }

    async function deleteSecondaryReferenceTransaction(
      position: number
    ): Promise<void> {
      try {
        await onSpinnerState();
        await deleteSecondaryReferenceOperation(position);
        await offSpinnerState();
        showSuccessModal("La referencia ha sido eliminada correctamente.");
      } catch (exception) {
        await offSpinnerState();
        throw exception;
      }
    }

    return {
      modalInfoSample,
      closeInformationSampleModal,
      modalInventoryImage,
      closeInventoryImageModal,
      references,
      showConfirmModal,
      openConfirmModal,
      closeConfirmModalAsync,
      confirmMessage,
      buildPrimaryReferenceTransaction,
      buildSecondaryReferenceTransaction,
      selectDeletedReference,
      deletePrimaryReferenceTransaction,
      deleteSecondaryReferenceTransaction,
      createNewReference,
      editReference,
      selectedReference,
      openInventoryImageModal,
      selectedAdditional,
      route,
    };
  },
});
</script>

<style scoped lang="scss">
@import "src/ux/sampleBook/styles/sampleBookStyle";
</style>
